import { H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import TopBadge from '@/components/Badges/TopBadge';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppSelector } from '@/redux/hooks';
import { getSellerLogoId, getSellerName } from '@/redux/modules/seller/seller.selectors';
import { isTopRatedHouse } from '@/redux/modules/topRatedSellers';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getInitialWidth } from '@/redux/modules/windowSize';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { getAuctioneerLogoUrl, getSellerUrl } from '@/utils/urls';

type Props = {
    className?: string;
    sellerId: number;
};

const { tabletNarrowWidthNum } = mergedCaterwaulAndHammerUIThemes.breakpoints;

const SellerInfo = ({ className, sellerId }: Props) => {
    const sellerName = useAppSelector((state) => getSellerName(state, sellerId));
    const sellerIsTopRated = useAppSelector((state) => isTopRatedHouse(state, sellerId));
    const logoId = useAppSelector((state) => getSellerLogoId(state, sellerId));
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const ssrWidth = useAppSelector(getInitialWidth);
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1, ssrWidth);
    const showSellerLogoLink = Boolean(logoId) && !whiteLabel && !isMobile;

    return (
        <div
            className={cn('mb-sm flex flex-row items-center justify-start', { 'justify-center': isMobile }, className)}
            data-testid="catalog-seller-info"
        >
            {showSellerLogoLink && (
                <div className="mr-sm flex h-[38px] w-[38px] items-center justify-center border border-border-secondary bg-background-primary">
                    <RouteLink
                        className="flex h-full w-full items-center justify-center"
                        data-testid="seller-info-logo"
                        to={getSellerUrl(sellerId, sellerName)}
                    >
                        <img
                            alt={sellerName}
                            className="max-h-[32px] max-w-[32px] object-contain"
                            height={32}
                            src={getAuctioneerLogoUrl(logoId, 80)}
                            width={32}
                        />
                    </RouteLink>
                </div>
            )}
            <div
                className={cn('mt-[6.5px] flex [&>div]:flex [&>div]:justify-center', {
                    'mt-[5px] ml-0 inline justify-center': isMobile,
                })}
            >
                <RouteLink
                    className="flex cursor-pointer no-underline tabletMax:mb-sm tabletMax:flex-col tabletMax:text-center"
                    to={getSellerUrl(sellerId, sellerName)}
                >
                    {isMobile ? (
                        <H2 data-testid="h2-seller-name">{sellerName}</H2>
                    ) : (
                        <H3 data-testid="h3-seller-name">{sellerName}</H3>
                    )}
                </RouteLink>
                {sellerIsTopRated && (
                    <TopBadge
                        className={cn('ml-xs align-middle', { 'ml-0 inline': isMobile })}
                        iconSize="small"
                    />
                )}
            </div>
        </div>
    );
};

export default SellerInfo;
